import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  Collapse,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Build as AssembleIcon,
  CallSplit as DisassembleIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from "@mui/icons-material";
import Table, { Column } from "../../components/Table/Table";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import { useAlert } from "../../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { useServerSidePagination } from "../../hooks/useServerSidePagination";
import { PaginatedResponse } from "../../types/api";
import { getTextFieldStyles } from "../../styles/formStyles";
import {
  bundlesApi,
  BundleDefinition,
  BundleInstance,
} from "../../services/bundlesApi";
import {
  productsApi,
  ProductApiResponse,
} from "../../services/productsApi";

interface ComponentRow {
  product: ProductApiResponse | null;
  quantity: number;
}

const cellSx = { borderColor: "rgba(255,255,255,0.12)", color: "white" };
const MS_IN_DAY = 24 * 60 * 60 * 1000;

function dateInputToDays(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((parsedDate.getTime() - today.getTime()) / MS_IN_DAY);
  return diffDays > 0 ? diffDays : undefined;
}

/** Dialog z tabelą produktów (paginacja + wyszukiwanie po stronie serwera). */
function ComponentProductPickerDialog({
  onClose,
  onSelect,
}: {
  onClose: () => void;
  onSelect: (product: ProductApiResponse) => void;
}) {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const { selectedWarehouse, loading: warehousesLoading } = useWarehouse();

  const fetchProducts = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<ProductApiResponse>> => {
      if (warehousesLoading || !selectedWarehouse?.id) {
        return { count: 0, next: null, previous: null, results: [] };
      }
      return productsApi.getProducts({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
        warehouse: selectedWarehouse.id,
      });
    },
    [selectedWarehouse?.id, warehousesLoading]
  );

  const pagination = useServerSidePagination(fetchProducts, {
    initialPageSize: 25,
    debounceMs: 400,
    onError: () => {
      showAlert("error", t("bundles.errors.productLoadFailed"));
    },
  });

  const columns: Column<ProductApiResponse>[] = useMemo(
    () => [
      { id: "name", label: t("products.columns.name"), flex: 1.5 },
      {
        id: "product_code",
        label: t("products.columns.barcode"),
        flex: 1,
        format: (v: string | undefined) => v || "—",
      },
      {
        id: "category",
        label: t("products.columns.category"),
        flex: 1,
        format: (v: string[]) =>
          Array.isArray(v) && v.length > 0 ? v.join(", ") : "—",
      },
      {
        id: "price",
        label: t("products.columns.price"),
        flex: 0.8,
        format: (v: string) => {
          const n = parseFloat(v);
          return Number.isNaN(n) ? "—" : `${n.toFixed(2)} ${t("common.currency")}`;
        },
      },
      {
        id: "quantity",
        label: t("products.columns.quantity"),
        flex: 0.7,
      },
    ],
    [t]
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#2a2a2a",
          color: "white",
          border: "1px solid #474747ff",
          borderRadius: "11px",
          maxHeight: "90vh",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {t("bundles.pickComponentProduct")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: "white" }}
          aria-label={t("common.close")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          px: 2,
          pt: 2,
          pb: 1,
          "&.MuiDialogContent-root": { paddingTop: 2 },
        }}
      >
        {!warehousesLoading && !selectedWarehouse?.id && (
          <Typography color="warning.main" sx={{ flexShrink: 0, pb: 2 }}>
            {t("bundles.selectWarehouseForProducts")}
          </Typography>
        )}
        <Box
          sx={{
            width: "100%",
            minHeight: 280,
            height: "calc(90vh - 240px)",
            maxHeight: "calc(90vh - 240px)",
            overflow: "hidden",
            // Table.tsx ustawia .MuiDataGrid-root .MuiDataGrid-columnHeaders na transparent (wyższa specyficzność) —
            // tu pełna ścieżka + !important oraz kontener sticky z v8 (container--top).
            "& .MuiDataGrid-container--top": {
              backgroundColor: "#2a2a2a !important",
              zIndex: 100,
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeaders": {
              backgroundColor: "#2a2a2a !important",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              zIndex: 100,
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeader": {
              backgroundColor: "#2a2a2a !important",
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
              backgroundColor: "#2a2a2a !important",
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller": {
              backgroundColor: "#2a2a2a !important",
            },
          }}
        >
          <Table<ProductApiResponse>
            columns={columns}
            data={pagination.data}
            page={pagination.page}
            rowsPerPage={pagination.pageSize}
            totalRows={pagination.totalCount}
            onPageChange={pagination.handlePageChange}
            onRowsPerPageChange={pagination.handlePageSizeChange}
            onSearchChange={pagination.handleSearchChange}
            rowKey="id"
            onRowClick={(row) => {
              onSelect(row);
              onClose();
            }}
            loading={pagination.isLoading || warehousesLoading}
            searchable
            searchPlaceholder={t("products.searchPlaceholder")}
            emptyMessage={t("bundles.noProductsInTable")}
            title=""
            description=""
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: "white" }}>
          {t("common.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Bundles = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const { selectedWarehouse } = useWarehouse();

  const [definitions, setDefinitions] = useState<BundleDefinition[]>([]);
  const [defsLoading, setDefsLoading] = useState(false);
  const [defsPage, setDefsPage] = useState(0);
  const [defsRowsPerPage, setDefsRowsPerPage] = useState(20);

  // Row expansion for history
  const [expandedDefId, setExpandedDefId] = useState<number | null>(null);
  const [historyInstances, setHistoryInstances] = useState<BundleInstance[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newShelfLifeDays, setNewShelfLifeDays] = useState("");
  const [defComponents, setDefComponents] = useState<ComponentRow[]>([
    { product: null, quantity: 1 },
  ]);
  const [assembleQty, setAssembleQty] = useState(1);
  const [createSaving, setCreateSaving] = useState(false);

  // Reassemble dialog
  const [reassembleOpen, setReassembleOpen] = useState(false);
  const [reassembleDef, setReassembleDef] = useState<BundleDefinition | null>(null);
  const [reassembleQty, setReassembleQty] = useState(1);
  const [reassembleSaving, setReassembleSaving] = useState(false);

  // Disassemble dialog
  const [disassembleOpen, setDisassembleOpen] = useState(false);
  const [disassembleInstance, setDisassembleInstance] = useState<BundleInstance | null>(null);

  /** Indeks wiersza komponentu z otwartym wyborem produktu (null = zamknięte). */
  const [productPickerRowIndex, setProductPickerRowIndex] = useState<number | null>(null);

  /** Ostatnie żądanie historii; starsze odpowiedzi są ignorowane (wyścig getInstances). */
  const historyFetchSeqRef = useRef(0);

  const loadDefinitions = useCallback(async () => {
    if (!selectedWarehouse?.id) {
      setDefinitions([]);
      return;
    }
    setDefsLoading(true);
    try {
      const data = await bundlesApi.getDefinitions({ warehouse: selectedWarehouse.id });
      setDefinitions(data);
    } catch {
      showAlert("error", t("bundles.errors.loadFailed") || "Nie udało się załadować danych pakietów");
    } finally {
      setDefsLoading(false);
    }
  }, [showAlert, t, selectedWarehouse?.id]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const loadHistory = useCallback(async (definitionId: number) => {
    const seq = ++historyFetchSeqRef.current;
    setHistoryLoading(true);
    try {
      const data = await bundlesApi.getInstances({ definition_id: definitionId });
      if (seq !== historyFetchSeqRef.current) return;
      setHistoryInstances(Array.isArray(data) ? data : []);
    } catch {
      if (seq !== historyFetchSeqRef.current) return;
      showAlert("error", "Nie udało się załadować historii pakietu");
    } finally {
      if (seq === historyFetchSeqRef.current) {
        setHistoryLoading(false);
      }
    }
  }, [showAlert]);

  const handleRowClick = useCallback(
    (row: BundleDefinition) => {
      if (expandedDefId === row.id) {
        setExpandedDefId(null);
        setHistoryInstances([]);
      } else {
        setExpandedDefId(row.id);
        loadHistory(row.id);
      }
    },
    [expandedDefId, loadHistory]
  );

  const openCreateDialog = () => {
    setNewName("");
    setNewCode("");
    setNewPrice("");
    setNewDescription("");
    setNewShelfLifeDays("");
    setDefComponents([{ product: null, quantity: 1 }]);
    setAssembleQty(1);
    setProductPickerRowIndex(null);
    setCreateOpen(true);
  };

  const handleCreateAndAssemble = async () => {
    if (!newName.trim() || !newCode.trim()) return;
    const validComponents = defComponents.filter((c) => c.product && c.quantity > 0);
    if (validComponents.length === 0) return;

    setCreateSaving(true);
    try {
      const parsedShelfLife = dateInputToDays(newShelfLifeDays);
      const definition = await bundlesApi.createWithProduct({
        name: newName.trim(),
        product_code: newCode.trim(),
        price: newPrice ? parseFloat(newPrice) : undefined,
        description: newDescription.trim() || undefined,
        warehouse_id: selectedWarehouse?.id,
        shelf_life_days: parsedShelfLife,
        components: validComponents.map((c) => ({
          component_product_id: c.product!.id,
          quantity_per_bundle: c.quantity,
        })),
      });

      if (assembleQty > 0) {
        const result = await bundlesApi.assemble({
          bundle_definition_id: definition.id,
          quantity: assembleQty,
        });
        showAlert(
          "success",
          `Pakiet utworzony i zmontowany (${assembleQty} szt.). Dokument: ${result.document_number}`
        );
      } else {
        showAlert("success", t("bundles.definitionCreated") || "Definicja pakietu została utworzona");
      }

      setCreateOpen(false);
      loadDefinitions();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t("bundles.errors.createFailed") || "Nie udało się utworzyć pakietu";
      showAlert("error", msg);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleReassemble = async () => {
    if (!reassembleDef || reassembleQty < 1) return;
    setReassembleSaving(true);
    try {
      const result = await bundlesApi.assemble({
        bundle_definition_id: reassembleDef.id,
        quantity: reassembleQty,
      });
      showAlert(
        "success",
        `Pakiet zmontowany (${reassembleQty} szt.). Dokument: ${result.document_number}`
      );
      setReassembleOpen(false);
      setReassembleDef(null);
      setReassembleQty(1);
      loadDefinitions();
      if (expandedDefId) loadHistory(expandedDefId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t("bundles.errors.assembleFailed") || "Nie udało się zmontować pakietu";
      showAlert("error", msg);
    } finally {
      setReassembleSaving(false);
    }
  };

  const handleDisassemble = async () => {
    if (!disassembleInstance) return;
    try {
      const result = await bundlesApi.disassemble({
        bundle_instance_id: disassembleInstance.id,
      });
      showAlert(
        "success",
        `Pakiet rozmontowany. Dokument: ${result.document_number}`
      );
      setDisassembleOpen(false);
      setDisassembleInstance(null);
      loadDefinitions();
      if (expandedDefId) loadHistory(expandedDefId);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t("bundles.errors.disassembleFailed") || "Nie udało się rozmontować pakietu";
      showAlert("error", msg);
    }
  };

  const defsColumns: Column<BundleDefinition>[] = useMemo(
    () => [
      {
        id: "bundle_product_name" as keyof BundleDefinition,
        label: t("bundles.columns.product") || "Nazwa",
        flex: 2,
        format: (val: unknown, row: BundleDefinition) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            {expandedDefId === row.id ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            <span>{val as string}</span>
          </Stack>
        ),
      },
      {
        id: "bundle_product_code" as keyof BundleDefinition,
        label: t("bundles.columns.code") || "Kod",
        flex: 1,
      },
      {
        id: "components" as keyof BundleDefinition,
        label: t("bundles.columns.components") || "Komponenty",
        flex: 2,
        format: (_: unknown, row: BundleDefinition) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {row.components.slice(0, 3).map((c) => (
              <Chip
                key={c.id}
                label={`${c.component_product_name} ×${c.quantity_per_bundle}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#474747ff", maxWidth: 180 }}
              />
            ))}
            {row.components.length > 3 && (
              <Chip label={`+${row.components.length - 3}`} size="small" variant="outlined" />
            )}
          </Stack>
        ),
      },
      {
        id: "total_assembled" as keyof BundleDefinition,
        label: t("bundles.columns.onStock") || "Na stanie",
        flex: 0.8,
        align: "right" as const,
        format: (val: unknown) => `${val || 0} szt.`,
      },
      {
        id: "active_instances_count" as keyof BundleDefinition,
        label: t("bundles.columns.totalAssembled") || "Zmontowano łącznie",
        flex: 0.8,
        align: "right" as const,
      },
      {
        id: "id" as keyof BundleDefinition,
        label: t("common.actions") || "Akcje",
        flex: 1.2,
        align: "right" as const,
        format: (_: unknown, row: BundleDefinition) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: "100%" }}>
            <Button
              size="small"
              startIcon={<AssembleIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setReassembleDef(row);
                setReassembleQty(1);
                setReassembleOpen(true);
              }}
              sx={{ color: "primary.main", textTransform: "none" }}
            >
              {t("bundles.assemble") || "Montuj"}
            </Button>
          </Stack>
        ),
      },
    ],
    [t, expandedDefId]
  );

  const textFieldStyles = getTextFieldStyles();

  const expandedDef = expandedDefId ? definitions.find((d) => d.id === expandedDefId) : null;

  return (
    <Box>
      <Typography color="white" variant="h5" fontWeight={600} mb={2}>
        {t("bundles.title") || "Pakiety"}
      </Typography>

      <Stack direction="row" spacing={1} mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          disabled={!selectedWarehouse?.id}
          sx={{ borderRadius: "11px", fontWeight: 600 }}
        >
          {t("bundles.createBundle") || "Utwórz pakiet"}
        </Button>
      </Stack>

      <Table<BundleDefinition>
        columns={defsColumns}
        data={definitions}
        page={defsPage}
        rowsPerPage={defsRowsPerPage}
        totalRows={definitions.length}
        onPageChange={setDefsPage}
        onRowsPerPageChange={setDefsRowsPerPage}
        rowKey="id"
        loading={defsLoading}
        onRowClick={handleRowClick}
        emptyMessage={t("bundles.noDefinitions") || "Brak pakietów. Kliknij 'Utwórz pakiet' aby dodać pierwszy."}
      />

      {/* Expanded history panel */}
      <Collapse in={!!expandedDefId && !!expandedDef} timeout="auto">
        {expandedDef && (
          <Box
            sx={{
              mt: 1,
              mb: 2,
              p: 2,
              bgcolor: "#1e1e1e",
              border: "1px solid #474747ff",
              borderRadius: "11px",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="white">
                {t("bundles.assemblyHistory") || "Historia montażu"} — {expandedDef.bundle_product_name}
              </Typography>
              <IconButton onClick={() => { setExpandedDefId(null); setHistoryInstances([]); }} sx={{ color: "white" }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {historyLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : historyInstances.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                {t("bundles.noHistory") || "Brak historii montażu/demontażu"}
              </Typography>
            ) : (
              <MuiTable size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("bundles.columns.quantity") || "Ilość"}</TableCell>
                    <TableCell>{t("bundles.columns.assembledAt") || "Data montażu"}</TableCell>
                    <TableCell>{t("bundles.columns.assembledBy") || "Zmontował"}</TableCell>
                    <TableCell>{t("bundles.columns.disassembledAt") || "Data rozmontowania"}</TableCell>
                    <TableCell>{t("bundles.columns.disassembledBy") || "Rozmontował"}</TableCell>
                    <TableCell>{t("bundles.columns.document") || "Dokument"}</TableCell>
                    <TableCell>{t("bundles.columns.instanceStatus") || "Status"}</TableCell>
                    <TableCell>{t("common.actions") || "Akcje"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyInstances.map((inst) => (
                    <TableRow key={inst.id}>
                      <TableCell>{inst.quantity} szt.</TableCell>
                      <TableCell>
                        {inst.created_at
                          ? new Date(inst.created_at).toLocaleString("pl-PL", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                          : "—"}
                      </TableCell>
                      <TableCell>{inst.created_by_name || "—"}</TableCell>
                      <TableCell>
                        {inst.disassembled_at
                          ? new Date(inst.disassembled_at).toLocaleString("pl-PL", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                          : "—"}
                      </TableCell>
                      <TableCell>{inst.disassembled_by_name || "—"}</TableCell>
                      <TableCell>{inst.document_number || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={inst.is_disassembled ? t("bundles.disassembledStatus") || "Rozmontowany" : t("bundles.assembledStatus") || "Zmontowany"}
                          size="small"
                          color={inst.is_disassembled ? "default" : "success"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {!inst.is_disassembled && (
                          <Button
                            size="small"
                            startIcon={<DisassembleIcon />}
                            onClick={() => {
                              setDisassembleInstance(inst);
                              setDisassembleOpen(true);
                            }}
                            sx={{ color: "warning.main", textTransform: "none" }}
                          >
                            {t("bundles.disassemble") || "Rozmontuj"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            )}
          </Box>
        )}
      </Collapse>

      {/* Create Bundle Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            color: "white",
            border: "1px solid #474747ff",
            borderRadius: "11px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("bundles.createBundleTitle") || "Utwórz pakiet"}
          <IconButton onClick={() => setCreateOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <Typography variant="subtitle2" color="text.secondary">
              1. {t("bundles.bundleInfo") || "Informacje o pakiecie"}
            </Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                sx={{ ...textFieldStyles, flex: 2 }}
                label={t("bundles.bundleName") || "Nazwa pakietu"}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
              />
              <TextField
                sx={{ ...textFieldStyles, flex: 1 }}
                label={t("bundles.bundleCode") || "Kod pakietu"}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                required
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                sx={{ ...textFieldStyles, flex: 1 }}
                label={t("bundles.bundlePrice") || "Cena (opcjonalnie)"}
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <TextField
                sx={{ ...textFieldStyles, flex: 2 }}
                label={t("bundles.bundleDescription") || "Opis (opcjonalnie)"}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </Stack>

            <TextField
              label={t("products.form.initialExpiryDate") || "Data ważności pakietu"}
              type="date"
              value={newShelfLifeDays}
              onChange={(e) => setNewShelfLifeDays(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                onClick: (e) => {
                  const input = (e.currentTarget as HTMLElement).querySelector('input[type="date"]') as HTMLInputElement | null;
                  input?.showPicker?.();
                  input?.focus();
                },
              }}
              helperText={
                t("bundles.shelfLifeDaysHelp") ||
                "Jeśli ustawione, przy każdym montażu pakietu stan magazynowy dostanie datę ważności liczoną od dnia montażu."
              }
              FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
              sx={{
                ...textFieldStyles,
                "& .MuiOutlinedInput-root": { cursor: "pointer" },
                "& .MuiInputBase-input": { cursor: "pointer" },
                "& .MuiInputBase-input::-webkit-calendar-picker-indicator": {
                  filter: "invert(1)",
                  cursor: "pointer",
                  opacity: 0.9,
                },
              }}
            />

            <Typography variant="subtitle2" color="text.secondary">
              2. {t("bundles.defineComponents") || "Zdefiniuj komponenty (z jakich produktów się składa)"}
            </Typography>

            {defComponents.map((comp, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <TextField
                  sx={{
                    ...textFieldStyles,
                    flex: 3,
                    cursor: "pointer",
                    "& .MuiInputBase-input": { cursor: "pointer" },
                  }}
                  label={`${t("bundles.component") || "Komponent"} ${i + 1}`}
                  value={
                    comp.product
                      ? `${comp.product.name}${comp.product.product_code ? ` (${comp.product.product_code})` : ""}`
                      : ""
                  }
                  placeholder={t("bundles.openProductTableHint")}
                  size="small"
                  InputProps={{ readOnly: true }}
                  onClick={() => setProductPickerRowIndex(i)}
                />
                <IconButton
                  size="small"
                  onClick={() => setProductPickerRowIndex(i)}
                  sx={{ color: "primary.main" }}
                  aria-label={t("bundles.pickProduct")}
                >
                  <SearchIcon />
                </IconButton>
                {comp.product && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const updated = [...defComponents];
                      updated[i] = { ...updated[i], product: null };
                      setDefComponents(updated);
                    }}
                    sx={{ color: "text.secondary" }}
                    aria-label={t("bundles.clearProduct")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <TextField
                  sx={{ ...textFieldStyles, flex: 1 }}
                  label={t("bundles.quantityPerBundle") || "Ilość/pakiet"}
                  type="number"
                  size="small"
                  value={comp.quantity}
                  onChange={(e) => {
                    const updated = [...defComponents];
                    updated[i] = { ...updated[i], quantity: Math.max(1, parseInt(e.target.value) || 1) };
                    setDefComponents(updated);
                  }}
                  inputProps={{ min: 1 }}
                />
                {defComponents.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => setDefComponents(defComponents.filter((_, j) => j !== i))}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                setDefComponents([...defComponents, { product: null, quantity: 1 }])
              }
              sx={{ color: "primary.main", alignSelf: "flex-start" }}
            >
              {t("bundles.addComponent") || "Dodaj komponent"}
            </Button>

            <Typography variant="subtitle2" color="text.secondary">
              3. {t("bundles.specifyQuantity") || "Ile pakietów zmontować od razu?"}
            </Typography>

            <TextField
              label={t("bundles.assembleQuantity") || "Ilość do montażu"}
              type="number"
              value={assembleQty}
              onChange={(e) => setAssembleQty(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              helperText={
                assembleQty === 0
                  ? "0 = tylko zapisz definicję, bez montażu"
                  : `Zmontuje ${assembleQty} szt. i utworzy dokument PA`
              }
              sx={textFieldStyles}
              FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
            />

            {newName && defComponents.some((c) => c.product) && assembleQty > 0 && (
              <Alert
                severity="info"
                sx={{
                  bgcolor: "rgba(255,255,255,0.05)",
                  border: "1px solid #474747ff",
                  "& .MuiAlert-icon": { color: "primary.main" },
                  "& .MuiAlert-message": { color: "white" },
                }}
              >
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  Podsumowanie montażu ({assembleQty} szt.):
                </Typography>
                {defComponents
                  .filter((c) => c.product)
                  .map((c, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 1 }}>
                      • {c.product!.name}: {c.quantity * assembleQty} szt. zostanie pobranych z magazynu
                    </Typography>
                  ))}
                <Typography variant="body2" sx={{ ml: 1, mt: 0.5 }}>
                  → {newName}: +{assembleQty} szt. zostanie dodanych na magazyn
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: "white" }}>
            {t("common.cancel") || "Anuluj"}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAndAssemble}
            disabled={createSaving || !selectedWarehouse?.id || !newName.trim() || !newCode.trim() || defComponents.every((c) => !c.product)}
            startIcon={<AssembleIcon />}
          >
            {createSaving ? (
              <CircularProgress size={24} />
            ) : assembleQty > 0 ? (
              `Utwórz i zmontuj (${assembleQty} szt.)`
            ) : (
              t("bundles.create") || "Utwórz"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {createOpen && productPickerRowIndex !== null && (
        <ComponentProductPickerDialog
          onClose={() => setProductPickerRowIndex(null)}
          onSelect={(product) => {
            const idx = productPickerRowIndex;
            setDefComponents((prev) => {
              const next = [...prev];
              if (idx >= 0 && idx < next.length) {
                next[idx] = { ...next[idx], product };
              }
              return next;
            });
            setProductPickerRowIndex(null);
          }}
        />
      )}

      {/* Reassemble dialog */}
      <Dialog
        open={reassembleOpen}
        onClose={() => setReassembleOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            color: "white",
            border: "1px solid #474747ff",
            borderRadius: "11px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("bundles.assembleTitle") || "Montaż pakietu"}
          <IconButton onClick={() => setReassembleOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            {reassembleDef && (
              <>
                <Typography variant="subtitle1" fontWeight={600}>
                  {reassembleDef.bundle_product_name} ({reassembleDef.bundle_product_code})
                </Typography>

                <Alert
                  severity="info"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.05)",
                    border: "1px solid #474747ff",
                    "& .MuiAlert-icon": { color: "primary.main" },
                    "& .MuiAlert-message": { color: "white" },
                  }}
                >
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    {t("bundles.componentsNeeded") || "Wymagane komponenty"} (na {reassembleQty} szt.):
                  </Typography>
                  {reassembleDef.components.map((c) => (
                    <Typography key={c.id} variant="body2" sx={{ ml: 1 }}>
                      • {c.component_product_name}: {c.quantity_per_bundle * reassembleQty} szt.
                    </Typography>
                  ))}
                </Alert>
              </>
            )}

            <TextField
              label={t("bundles.quantity") || "Ilość pakietów"}
              type="number"
              value={reassembleQty}
              onChange={(e) => setReassembleQty(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={textFieldStyles}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReassembleOpen(false)} sx={{ color: "white" }}>
            {t("common.cancel") || "Anuluj"}
          </Button>
          <Button
            variant="contained"
            onClick={handleReassemble}
            disabled={reassembleSaving || !reassembleDef}
            startIcon={<AssembleIcon />}
          >
            {reassembleSaving ? <CircularProgress size={24} /> : `${t("bundles.assemble") || "Montuj"} (${reassembleQty} szt.)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disassemble Confirm Dialog */}
      <CustomPopup
        open={disassembleOpen}
        onClose={() => {
          setDisassembleOpen(false);
          setDisassembleInstance(null);
        }}
        title={t("bundles.disassembleTitle") || "Rozmontuj pakiet"}
        text={
          disassembleInstance
            ? `Czy na pewno chcesz rozmontować pakiet "${disassembleInstance.definition_name}" (${disassembleInstance.quantity} szt.)? Komponenty zostaną zwrócone na magazyn i powstanie dokument RO.`
            : ""
        }
        confirmText={t("bundles.disassemble") || "Rozmontuj"}
        onConfirm={handleDisassemble}
        confirmButtonColor="warning"
      />
    </Box>
  );
};

export default Bundles;
